import { isTuningIdentity, isTuningReceipt, isTuningTrialSnapshot } from './identity';
import type { TuningIdentity, TuningReceipt, TuningTrialSnapshot } from './identity';
import { isTuningTelemetry } from './measurements';
import type { TuningTelemetryWindow } from './measurements';
import { isTuningConfigurationHash, isTuningPatch, TUNING_STRATEGY_IDS } from './settings';
import type { TuningPatch } from './settings';
import { hasOnlyTuningKeys, isTuningArray, isTuningEnum, isTuningError, isTuningInteger, isTuningNumber,
  isTuningRecord, isTuningString, tuningJsonBytes } from './validation';

export type TuningTargets = {
  minPresentedFraction: number; maxGapP99Ms: number; maxGapMs: number;
  maxLatencyP95Ms: number; maxTelemetryDropFraction: number;
  latencyEndpoint: 'capture-to-composition' | 'encode-to-composition';
};
export type TuningExperimentSpec = {
  id: string; kind: 'comparison' | 'qualification'; sessionId: string; workloadId: string;
  candidates: readonly { strategyId: string; patch: TuningPatch }[];
  repetitions: number; settleMs: number; measureMs: number; totalDeadlineMs: number;
  maxFreshAttempts: number; targets: TuningTargets; restoreOnFailure: boolean;
};
export type TuningTrialScore = {
  trialId: string; configurationHash: string; result: 'pass' | 'fail' | 'inconclusive' | 'invalid';
  reasonCodes: readonly string[]; coverageFraction: number; presentedFraction?: number;
  gapP95Ms?: number; gapP99Ms?: number; maxGapMs?: number; latencyP50Ms?: number; latencyP95Ms?: number; latencyP99Ms?: number;
  quantileBounds: Readonly<Record<string, { lowerMs: number; upperMs: number | null }>>;
  latencyEndpoint?: TuningTargets['latencyEndpoint'];
  imageQuality: { kind: 'unmeasured' | 'subjective' | 'reference-metric'; value?: number };
};
export type TuningExperimentRequest = {
  version: 1; requestId: string; sessionId: string; leaseId: string;
  ttlMs: number; expectedRevision: number; spec: TuningExperimentSpec;
};
export type TuningExperimentSnapshot = {
  id: string; sessionId: string; requestId: string;
  state: 'queued' | 'running' | 'restoring' | 'completed' | 'failed' | 'cancelled';
  currentTrialId?: string; startedAtEpochMs: number; deadlineAtEpochMs: number;
  scores: readonly TuningTrialScore[]; error?: { code: string; message: string };
};
export interface IExperimentRunner {
  start(request: TuningExperimentRequest): Promise<TuningExperimentSnapshot>;
  status(id: string): TuningExperimentSnapshot | undefined;
  cancel(id: string, reason: string): Promise<TuningExperimentSnapshot>;
}
export type TuningSessionLease = {
  sessionId: string; peerId: string; leaseId: string; expiresAtHostMonotonicMs: number;
};
export type TuningLocalEvent = {
  sequence: number; target: TuningIdentity; endpoint: 'host' | 'guest';
  kind: 'receipt' | 'telemetry' | 'trial' | 'experiment' | 'lease-revoked';
  payload: TuningReceipt | TuningTelemetryWindow | TuningTrialSnapshot | TuningExperimentSnapshot | { reason: string };
};
export type TuningLocalEventPage = {
  events: readonly TuningLocalEvent[]; nextCursor: number; oldestAvailableCursor: number; gap: boolean;
};

export function isTuningTargets(value: unknown): value is TuningTargets {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, ['minPresentedFraction', 'maxGapP99Ms', 'maxGapMs',
    'maxLatencyP95Ms', 'maxTelemetryDropFraction', 'latencyEndpoint']) &&
    isTuningNumber(value['minPresentedFraction'], 0, 1) && isTuningNumber(value['maxTelemetryDropFraction'], 0, 1) &&
    isTuningNumber(value['maxGapP99Ms'], 0, 120_000) && isTuningNumber(value['maxGapMs'], value['maxGapP99Ms'], 120_000) &&
    isTuningNumber(value['maxLatencyP95Ms'], 0, 120_000) &&
    isTuningEnum(value['latencyEndpoint'], ['capture-to-composition', 'encode-to-composition']);
}

function isCandidate(value: unknown): boolean {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, ['strategyId', 'patch']) &&
    typeof value['strategyId'] === 'string' && TUNING_STRATEGY_IDS.includes(value['strategyId']) && isTuningPatch(value['patch']);
}

export function isTuningExperimentSpec(value: unknown): value is TuningExperimentSpec {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value, ['id', 'kind', 'sessionId', 'workloadId', 'candidates',
    'repetitions', 'settleMs', 'measureMs', 'totalDeadlineMs', 'maxFreshAttempts', 'targets', 'restoreOnFailure'])) return false;
  const comparison = value['kind'] === 'comparison';
  const maxAttempts = comparison ? 12 : 36;
  if (!comparison && value['kind'] !== 'qualification') return false;
  if (!isTuningString(value['id']) || !isTuningString(value['sessionId']) || !isTuningString(value['workloadId']) ||
    !isTuningArray(value['candidates'], 12, isCandidate) || value['candidates'].length === 0 ||
    !isTuningInteger(value['repetitions'], 1, maxAttempts) || !isTuningNumber(value['settleMs'], 0, 60_000) ||
    !isTuningNumber(value['measureMs'], 1, 120_000) || !isTuningInteger(value['maxFreshAttempts'], 1, maxAttempts) ||
    !isTuningNumber(value['totalDeadlineMs'], 1, comparison ? 600_000 : 1_500_000) ||
    !isTuningTargets(value['targets']) || typeof value['restoreOnFailure'] !== 'boolean') return false;
  const planned = value['candidates'].length * value['repetitions'] * (comparison ? 3 : 1);
  return planned <= value['maxFreshAttempts'] &&
    planned * (value['settleMs'] + value['measureMs'] + 15_000) <= value['totalDeadlineMs'];
}

export function isTuningExperimentRequest(value: unknown): value is TuningExperimentRequest {
  return isTuningRecord(value) && hasOnlyTuningKeys(value,
    ['version', 'requestId', 'sessionId', 'leaseId', 'ttlMs', 'expectedRevision', 'spec']) && value['version'] === 1 &&
    isTuningString(value['requestId']) && isTuningString(value['sessionId']) && isTuningString(value['leaseId']) &&
    isTuningNumber(value['ttlMs'], 100, 10_000) && isTuningInteger(value['expectedRevision']) &&
    isTuningExperimentSpec(value['spec']) && value['sessionId'] === value['spec'].sessionId && tuningJsonBytes(value) <= 16_384;
}

export function parseTuningExperimentRequest(value: unknown): TuningExperimentRequest | undefined {
  return isTuningExperimentRequest(value) ? value : undefined;
}

function isQuantileBounds(value: unknown): boolean {
  return isTuningRecord(value) && Object.keys(value).length <= 32 && Object.entries(value).every(([key, bounds]) =>
    isTuningString(key) && isTuningRecord(bounds) && hasOnlyTuningKeys(bounds, ['lowerMs', 'upperMs']) &&
    isTuningNumber(bounds['lowerMs']) && (bounds['upperMs'] === null || isTuningNumber(bounds['upperMs'], bounds['lowerMs'])));
}

export function isTuningTrialScore(value: unknown): value is TuningTrialScore {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value, ['trialId', 'configurationHash', 'result', 'reasonCodes',
    'coverageFraction', 'presentedFraction', 'gapP95Ms', 'gapP99Ms', 'maxGapMs', 'latencyP50Ms', 'latencyP95Ms',
    'latencyP99Ms', 'quantileBounds', 'latencyEndpoint', 'imageQuality'])) return false;
  const quality = value['imageQuality'];
  return isTuningString(value['trialId']) && isTuningConfigurationHash(value['configurationHash']) &&
    isTuningEnum(value['result'], ['pass', 'fail', 'inconclusive', 'invalid']) &&
    isTuningArray(value['reasonCodes'], 32, (item) => isTuningString(item)) && isTuningNumber(value['coverageFraction'], 0, 1) &&
    (value['presentedFraction'] === undefined || isTuningNumber(value['presentedFraction'], 0, 2)) &&
    ['gapP95Ms', 'gapP99Ms', 'maxGapMs', 'latencyP50Ms', 'latencyP95Ms', 'latencyP99Ms']
      .every((key) => value[key] === undefined || isTuningNumber(value[key])) && isQuantileBounds(value['quantileBounds']) &&
    (value['latencyEndpoint'] === undefined || isTuningEnum(value['latencyEndpoint'], ['capture-to-composition', 'encode-to-composition'])) &&
    isTuningRecord(quality) && hasOnlyTuningKeys(quality, ['kind', 'value']) &&
    isTuningEnum(quality['kind'], ['unmeasured', 'subjective', 'reference-metric']) &&
    (quality['value'] === undefined || isTuningNumber(quality['value'])) && !(quality['kind'] === 'unmeasured' && quality['value'] !== undefined);
}

export function isTuningExperimentSnapshot(value: unknown): value is TuningExperimentSnapshot {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, ['id', 'sessionId', 'requestId', 'state',
    'currentTrialId', 'startedAtEpochMs', 'deadlineAtEpochMs', 'scores', 'error']) && isTuningString(value['id']) &&
    isTuningString(value['sessionId']) && isTuningString(value['requestId']) &&
    isTuningEnum(value['state'], ['queued', 'running', 'restoring', 'completed', 'failed', 'cancelled']) &&
    (value['currentTrialId'] === undefined || isTuningString(value['currentTrialId'])) && isTuningNumber(value['startedAtEpochMs']) &&
    isTuningNumber(value['deadlineAtEpochMs'], value['startedAtEpochMs']) && isTuningArray(value['scores'], 36, isTuningTrialScore) &&
    (value['error'] === undefined || isTuningError(value['error']));
}

export function isTuningSessionLease(value: unknown): value is TuningSessionLease {
  return isTuningRecord(value) && hasOnlyTuningKeys(value, ['sessionId', 'peerId', 'leaseId', 'expiresAtHostMonotonicMs']) &&
    isTuningString(value['sessionId']) && isTuningString(value['peerId']) && isTuningString(value['leaseId']) && isTuningNumber(value['expiresAtHostMonotonicMs']);
}

export function isTuningLocalEvent(value: unknown): value is TuningLocalEvent {
  if (!isTuningRecord(value) || !hasOnlyTuningKeys(value, ['sequence', 'target', 'endpoint', 'kind', 'payload']) ||
    !isTuningInteger(value['sequence'], 1) || !isTuningIdentity(value['target']) || !['host', 'guest'].includes(String(value['endpoint']))) return false;
  switch (value['kind']) {
    case 'receipt': return isTuningReceipt(value['payload']);
    case 'telemetry': return isTuningTelemetry(value['payload']);
    case 'trial': return isTuningTrialSnapshot(value['payload']);
    case 'experiment': return isTuningExperimentSnapshot(value['payload']);
    case 'lease-revoked': return isTuningRecord(value['payload']) && hasOnlyTuningKeys(value['payload'], ['reason']) && isTuningString(value['payload']['reason'], 2_048);
    default: return false;
  }
}
